import { useEffect, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  HeartIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'
import type { CompetitorAd } from '../../client'
import { ageRange, isVideoAd, platform, runningTime } from '../../lib/competitorAds'

const ROUND_BUTTON =
  'flex size-7 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white backdrop-blur-xl transition hover:scale-105 hover:bg-black/60'
const ACTION =
  'inline-flex w-full items-center justify-center gap-1 rounded-lg bg-purple-100 py-2 text-xs font-medium text-purple-600 ring-1 ring-purple-600 transition ring-inset hover:bg-purple-200'
const PLAY_RATIO = 0.4

function Clapperboard({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z" />
      <path d="m6.2 5.3 3.1 3.9" />
      <path d="m12.4 3.4 3.1 4" />
      <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </svg>
  )
}

type Props = {
  ad: CompetitorAd
  logo?: string | null
  onToggleFavourite: (ad: CompetitorAd) => void
  onClone: (ad: CompetitorAd, cardIndex: number) => void
}

export default function CompetitorAdCard({ ad, logo, onToggleFavourite, onClone }: Props) {
  const cards = ad.cards ?? []
  const [cardIndex, setCardIndex] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [nearViewport, setNearViewport] = useState(false)
  const [onScreen, setOnScreen] = useState(false)
  const [mediaLoaded, setMediaLoaded] = useState(false)
  const mediaRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const card = cards[cardIndex]
  const image = card?.image_url || card?.video_preview_image_url || ''
  const video = card?.video_url ?? ''
  const isVideo = isVideoAd(ad)
  const audience = [ageRange(ad) && `Age: ${ageRange(ad)}`, ad.gender_audience && `Gender: ${ad.gender_audience}`]
    .filter(Boolean)
    .join(' · ')

  useEffect(() => {
    const media = mediaRef.current
    if (!media) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setNearViewport(true)
          setOnScreen(entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO)
        }
      },
      { threshold: [0, PLAY_RATIO, 1], rootMargin: '200px 0px' },
    )

    observer.observe(media)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const player = videoRef.current
    if (!player) return

    if (onScreen) void player.play().catch(() => undefined)
    else player.pause()
  }, [onScreen, video, mediaLoaded])

  function step(by: number) {
    setMediaLoaded(false)
    setCardIndex((current) => (current + by + cards.length) % cards.length)
  }

  return (
    <div className="mb-3 break-inside-avoid overflow-hidden rounded-xl bg-stone-100 shadow-sm ring-1 ring-slate-200/60">
      <div
        ref={mediaRef}
        onClick={() => setPreviewOpen(true)}
        onMouseLeave={() => setExpanded(false)}
        className="group relative cursor-pointer bg-stone-200"
      >
        {!mediaLoaded && (video || image) && <div className="aspect-4/5 w-full animate-pulse bg-slate-200" />}

        {video ? (
          <video
            ref={videoRef}
            src={nearViewport ? video : undefined}
            poster={nearViewport ? image || undefined : undefined}
            onLoadedMetadata={() => setMediaLoaded(true)}
            className={mediaLoaded ? 'block w-full' : 'hidden'}
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : image ? (
          <img
            src={nearViewport ? image : undefined}
            alt={ad.page_name ?? 'Ad preview'}
            onLoad={() => setMediaLoaded(true)}
            decoding="async"
            className={mediaLoaded ? 'block w-full' : 'hidden'}
          />
        ) : (
          <div className="flex aspect-square items-center justify-center text-xs text-slate-400">No preview</div>
        )}

        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 pointer-coarse:opacity-100">
          <button
            type="button"
            aria-label={ad.is_favourite ? 'Unfavourite' : 'Favourite'}
            data-tooltip={ad.is_favourite ? 'Unfavourite' : 'Favourite'}
            data-tooltip-start=""
            onClick={(event) => {
              event.stopPropagation()
              onToggleFavourite(ad)
            }}
            className={ROUND_BUTTON}
          >
            {ad.is_favourite ? (
              <HeartSolidIcon className="size-3.5 text-red-500" />
            ) : (
              <HeartIcon className="size-3.5" />
            )}
          </button>
          {(video || image) && (
            <a
              href={video || image}
              target="_blank"
              rel="noreferrer"
              aria-label="Download"
              data-tooltip="Download"
              data-tooltip-start=""
              onClick={(event) => event.stopPropagation()}
              className={ROUND_BUTTON}
            >
              <ArrowDownTrayIcon className="size-3.5" />
            </a>
          )}
        </div>

        {cards.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={(event) => {
                event.stopPropagation()
                step(-1)
              }}
              className={`${ROUND_BUTTON} absolute top-1/2 left-2 size-6 -translate-y-1/2`}
            >
              <ChevronLeftIcon className="size-3.5" />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={(event) => {
                event.stopPropagation()
                step(1)
              }}
              className={`${ROUND_BUTTON} absolute top-1/2 right-2 size-6 -translate-y-1/2`}
            >
              <ChevronRightIcon className="size-3.5" />
            </button>
            <div className="absolute bottom-12 left-1/2 flex -translate-x-1/2 gap-1">
              {cards.map((_, index) => (
                <span
                  key={index}
                  className={`size-1.5 rounded-full ${index === cardIndex ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}

        {expanded && (
          <div
            onClick={(event) => {
              event.stopPropagation()
              setExpanded(false)
            }}
            className="absolute inset-0 bg-black/40 transition-opacity duration-300"
          />
        )}

        <div
          onClick={(event) => event.stopPropagation()}
          className={`absolute right-0 bottom-0 left-0 flex flex-col overflow-hidden bg-linear-to-t from-black/80 via-black/50 to-transparent px-3 pt-6 pb-2.5 transition-all duration-300 ease-out ${
            expanded ? 'max-h-[70%]' : 'max-h-26'
          }`}
        >
          <div className="flex items-center gap-2">
            {logo && (
              <img
                src={logo}
                alt=""
                referrerPolicy="no-referrer"
                className="size-5 shrink-0 rounded-full object-cover ring-1 ring-white/30"
              />
            )}
            <span className="truncate text-sm font-semibold text-white">{ad.page_name || 'Unknown'}</span>
          </div>
          {ad.body_text && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className={`mt-1 text-left text-xs leading-relaxed text-white/90 ${
                expanded ? 'overflow-y-auto [scrollbar-width:none]' : 'line-clamp-1'
              }`}
            >
              {ad.body_text}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 border-t border-stone-200 px-2.5 py-2.5 text-xs text-slate-500">
        <div className="flex items-center gap-2.5">
          <span className="flex shrink-0 items-center gap-1.5">
            <span className={`inline-block size-2 rounded-full ${ad.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {ad.is_active ? 'Active' : 'Inactive'}
          </span>
          {ad.impressions_text && (
            <span className="impressions-marquee min-w-0 flex-1 overflow-hidden font-medium whitespace-nowrap text-slate-600">
              <span>{ad.impressions_text} impressions</span>
            </span>
          )}
          {audience && <span className="ml-auto shrink-0 whitespace-nowrap">{audience}</span>}
        </div>

        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5">
            {(ad.publisher_platform ?? []).map((name) => {
              const { label, path } = platform(name)
              return (
                <span key={name} data-tooltip={label} data-tooltip-start="" className="flex">
                  <svg viewBox="0 0 24 24" fill="#94a3b8" className="size-4" role="img" aria-label={label}>
                    <path d={path} />
                  </svg>
                </span>
              )
            })}
          </div>
          {runningTime(ad) && (
            <span className="ml-auto" title={ad.start_date_formatted}>
              Running time: {runningTime(ad)}
            </span>
          )}
        </div>
      </div>

      <div className="bg-stone-100 px-3 pb-2.5">
        <button
          type="button"
          aria-disabled={isVideo}
          onClick={() => !isVideo && onClone(ad, cardIndex)}
          className={`${ACTION} group/action`}
        >
          {isVideo ? (
            <>
              <ClockIcon className="mr-1 hidden size-4 text-purple-600 group-hover/action:block" />
              <Clapperboard className="mr-1 size-4 text-purple-600 group-hover/action:hidden" />
              <span className="group-hover/action:hidden">Recreate Ad</span>
              <span className="hidden group-hover/action:inline">Coming soon</span>
            </>
          ) : (
            <>
              <SparklesIcon className="mr-1 size-4 text-purple-600" />
              Clone Ad
            </>
          )}
        </button>
      </div>

      {previewOpen && (video || image) && (
        <div
          onClick={() => setPreviewOpen(false)}
          className="fixed inset-0 z-[220] flex items-center justify-center bg-black/80"
        >
          <div onClick={(event) => event.stopPropagation()} className="relative max-h-[90vh] max-w-[90vw]">
            <button
              type="button"
              aria-label="Close preview"
              onClick={() => setPreviewOpen(false)}
              className="absolute top-2 right-2 z-10 rounded-full bg-black/60 p-1.5 transition hover:bg-black/80"
            >
              <XMarkIcon className="size-4 text-white" />
            </button>
            {video ? (
              <video
                src={video}
                poster={image || undefined}
                className="block max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                controls
                autoPlay
                loop
                playsInline
              />
            ) : (
              <img
                src={image}
                alt="Full size preview"
                className="block max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
